import {
  createEffect,
  createMemo,
  createRoot,
  createSignal,
  onCleanup,
  on,
  observable,
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import { insert } from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/web/+esm";
import {
  register,
  compose,
  noShadowDOM,
} from "https://cdn.jsdelivr.net/npm/component-register@0.8.3/+esm";

import KeenSlider from "https://cdn.jsdelivr.net/npm/keen-slider/+esm";

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner)
    return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (
    next &&
    !next._$owner &&
    !(next.assignedSlot && next.assignedSlot._$owner)
  )
    next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = "";
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(Component) {
  return (props, options) => {
    noShadowDOM();
    const comp = Component(props, options);
    return comp;
  };
}

const customShadowlessElement = (
  tagName,
  props = {},
  Component = (props, options) => {},
  ...rest
) =>
  compose(
    register(tagName, props),
    withNoShadowDOM,
    withSolid,
    ...rest,
  )(Component);

function initializeSliderClasses(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.classList.add("keen-slider");
    Array.from(element.children).forEach((child) =>
      child.classList.add("keen-slider__slide"),
    );
    return ComponentType(props, options);
  };
}

function constrain(value, min = -Infinity, max = Infinity) {
  return Math.min(Math.max(value, min), max);
}

function attachMoreDetails(prevSlide) {
  return function (slider) {
    if (slider.track.details.rel != slider.animator.targetIdx) return;

    slider.direction =
      slider.track.details.rel > prevSlide ? "forward" : "backward";

    prevSlide = slider.track.details.rel;
  };
}

function attachAriaAttributes(slider) {
  const maxIdx =
    slider?.track?.details?.maxIdx === Infinity
      ? slider.track.details.length
      : slider?.track?.details?.maxIdx || 0;
  slider.container.setAttribute("role", "listbox");
  slider.container.setAttribute(
    "aria-orientation",
    slider.options.vertical ? "vertical" : "horizontal",
  );
  slider.container.setAttribute("aria-multiselectable", "false");
  slider.container.setAttribute("aria-label", "Slider");
  slider.container.setAttribute("tabindex", "0");
  slider.container.setAttribute("aria-live", "polite");
  slider.container.setAttribute("aria-atomic", "true");
  slider.container.setAttribute("aria-live", "polite");
  slider.container.setAttribute("aria-busy", "false");
  slider.container.setAttribute("aria-valuemin", "0");
  slider.container.setAttribute("aria-valuemax", maxIdx);
  slider.container.setAttribute(
    "aria-valuenow",
    slider?.track?.details?.rel || 0,
  );

  function getMaxRel(rel, perView = 1) {
    return Math.floor(rel + perView - 1);
  }

  function slideIsActive(idx) {
    const perView = slider.options?.slides?.perView || 1,
      rel = slider.track.details.rel,
      maxRel = getMaxRel(rel, perView);

    return rel <= idx && idx <= maxRel;
  }

  async function updateTabIndexes(slide, tabindex) {
    const focusableElements = Array.from(
      slide.querySelectorAll(
        "input, a, button:not(:disabled), textarea, select, textarea, [tabindex]",
      ),
    );
    focusableElements.forEach((element) => {
      element.setAttribute("tabindex", tabindex);
    });
  }

  function updateAriaActiveSlide(slide) {
    slide.setAttribute("aria-selected", "true");
    slide.setAttribute("aria-hidden", "false");
    slide.setAttribute("tabindex", "0");
    updateTabIndexes(slide, "0");
  }

  function updateAriaInActiveSlide(slide) {
    slide.setAttribute("aria-selected", "false");
    slide.setAttribute("aria-hidden", "true");
    slide.setAttribute("tabindex", "-1");
    updateTabIndexes(slide, "-1");
  }

  function updateSlideAriaAttributes(slider) {
    if (slider.options.disabled) {
      slider.slides.forEach((slide, i) => {
        slide.setAttribute("role", "option");
        updateAriaActiveSlide(slide);
      });
      return;
    }

    slider.slides.forEach((slide, i) => {
      slide.setAttribute("role", "option");
      if (slideIsActive(i)) updateAriaActiveSlide(slide);
      else updateAriaInActiveSlide(slide);
    });
  }

  slider.on("slideChanged", updateSlideAriaAttributes);
  slider.on("created", updateSlideAriaAttributes);
  slider.on("optionsChanged", updateSlideAriaAttributes);
  slider.on("updated", updateSlideAriaAttributes);
}

class CustomKeenSlider extends KeenSlider {
  constructor(element, config, plugins = []) {
    super(element, config, plugins);
    let prevSlide = 0;

    this.direction = null;
    this.on("slideChanged", attachMoreDetails(prevSlide));

    attachAriaAttributes(this);

    this.next = function () {
      const amount = this.options?.slides?.perScroll || 1;
      if (!this.options.loop) {
        return this.moveToIdx(
          constrain(
            this.track.details.rel + amount,
            0,
            this.track.details.maxIdx,
          ),
        );
      }
      return this.moveToIdx(constrain(this.track.details.abs + amount), true);
    };

    this.prev = function () {
      const amount = this.options?.slides?.perScroll || 1;
      if (!this.options.loop) {
        this.moveToIdx(
          constrain(
            this.track.details.rel - amount,
            0,
            this.track.details.maxIdx,
          ),
        );
      }
      return this.moveToIdx(constrain(this.track.details.abs - amount), true);
    };
  }
}

function Component(props, { element }) {
  const [plugins, setPlugins] = createSignal([]);
  const [forceSliderRebuild, setForceSliderRebuild] = createSignal(false);

  const mutObserver = createMemo(() => {
    if (props.refreshOnChildrenChange)
      return new MutationObserver(function (mutations) {
        mutations.forEach(function (_) {
          setForceSliderRebuild((v) => !v);
        });
      });
    return null;
  });

  const slider = createMemo(() => {
    element.setAttribute("data-initialized", true);
    const slider = new CustomKeenSlider(element, props.config || {}, plugins());
    const m = mutObserver();

    if (m) {
      m.observe(slider.container, { childList: true });
      forceSliderRebuild();
    }

    onCleanup(() => {
      slider.destroy();
      if (m) m.disconnect();
    });

    return slider;
  });

  const observer = observable(slider);

  Object.assign(element, {
    subscribe(callback = (slider) => null) {
      function listen(slider) {
        const cleanup = callback(slider);
        slider.on(
          "destroyed",
          typeof cleanup === "function" ? cleanup : () => null,
        );
      }
      return observer.subscribe(listen).unsubscribe;
    },
  });

  Object.assign(element, { slider });
  Object.assign(element, { addPlugin });

  function addPlugin(plugin) {
    let index = -1;
    setPlugins((plugins) => {
      index = plugins.length;
      return [...plugins, plugin];
    });

    return function () {
      setPlugins((plugins) => {
        return [...plugins.slice(0, index), ...plugins.slice(index + 1)];
      });
    };
  }

  function centeredPlugin(slider) {
    function centerSlides(slider) {
      if (!slider.options.center) {
        slider.container.style.justifyContent = null;
        slider.container.style.transform = null;
        return;
      }

      if (slider.track.details.maxIdx > 0) {
        slider.container.style.justifyContent = null;
        slider.container.style.transform = null;
        return;
      }

      slider.container.style.cssText = `
          justify-content: center;
          transform: translateX(-${slider.options.slides.spacing || 16}px)
        `;
    }

    slider.on("created", centerSlides);
    slider.on("optionsChanged", centerSlides);
  }

  createEffect(
    on(
      () => props.centerSlides,
      (centerSlides) => {
        if (!centerSlides) return;
        const remove = addPlugin(centeredPlugin);
        return onCleanup(remove);
      },
    ),
  );

  createEffect(
    on(plugins, (plugins) => {
      if (plugins.length === 0) return;
      console.log(
        `Registered a new plugin on element.\n`,
        `Total slider plugins: ${plugins.length}`,
      );
    }),
  );
}

customShadowlessElement(
  "keen-slider",
  { config: {}, centerSlides: false, refreshOnChildrenChange: false },
  Component,
  initializeSliderClasses,
);
